<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { wantsTouch } from '@/game/input-source';
import { useDevicePrompt } from '@/state/device-prompts';

/** `height >= width` on the viewport. See the header for why not `screen`. */
const PORTRAIT_QUERY = '(orientation: portrait)';

/**
 * Only the game itself — the garage and a battle — has no upright layout.
 * The landing page, the sign-in/register form and every other screen the
 * player can reach before that are ordinary responsive pages and must stay
 * reachable in portrait; `App.vue` passes `phase === 'garage' || 'playing'`.
 * Without this the prompt covered the marketing page and the auth form too,
 * which is what made the auth screen "not work" on a phone held upright —
 * there was nothing wrong with the form, it was never reachable.
 */
const props = defineProps<{ active: boolean }>();

const portrait = ref(false);
const touch = ref(false);

/** Both halves, read together, so they can never disagree about one moment. */
function reread(): void {
  touch.value = wantsTouch();
  portrait.value =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(PORTRAIT_QUERY).matches;
}

const showing = computed(() => props.active && touch.value && portrait.value);

/*
 * Announced, so anything that would otherwise draw underneath waits its turn.
 *
 * This prompt states a fact and offers no choice, so it is the *first* question
 * in the queue and the one everything else defers to — see
 * `state/device-prompts.ts` for the dead button that came of nothing doing so.
 */
useDevicePrompt(showing);

let media: MediaQueryList | null = null;

onMounted(() => {
  if (typeof window === 'undefined') return;
  reread();
  if (typeof window.matchMedia === 'function') {
    media = window.matchMedia(PORTRAIT_QUERY);
    media.addEventListener('change', reread);
  }
  window.addEventListener('resize', reread);
  window.addEventListener('orientationchange', reread);
  window.addEventListener('sow:input-source', reread);
});

onBeforeUnmount(() => {
  media?.removeEventListener('change', reread);
  if (typeof window === 'undefined') return;
  window.removeEventListener('resize', reread);
  window.removeEventListener('orientationchange', reread);
  window.removeEventListener('sow:input-source', reread);
});

/*
 * The mark: the same device drawn twice, upright and ghosted, sideways and lit,
 * about one centre, with an arc over the top saying which way.
 *
 * Drawn rather than fetched, for the reason the radar bezel, the title badges,
 * the key caps, the gear and the currency mark are all drawn: the game renders at
 * `devicePixelRatio`, and this is the first thing a phone player ever sees, so a
 * fixed raster would be soft on exactly the displays it is aimed at. There is
 * nothing here a raster would buy either — two rounded rectangles and a stroke.
 *
 * Two phones rather than one animated one, because the *static* frame has to
 * carry the whole message: `prefers-reduced-motion` gets this picture with
 * nothing moving, and a single phone frozen mid-turn says nothing at all.
 */
/*
 * Both boxes are **concentric on (60, 48)** — 44+32/2 = 60 and 20+56/2 = 48, and
 * 32+56/2 = 60 and 32+32/2 = 48. That is the one property of these four numbers
 * that carries the meaning: off-centre they stop being one device turning and
 * become two devices side by side, which is a completely different picture and a
 * perfectly plausible-looking one. Named rather than inlined into the template so
 * the arithmetic is visible next to the claim.
 */
const PHONE_UPRIGHT = { x: 44, y: 20, width: 32, height: 56 } as const;
const PHONE_SIDEWAYS = { x: 32, y: 32, width: 56, height: 32 } as const;
</script>

<template>
  <div
    v-if="showing"
    class="rotate"
    data-test="rotate-prompt"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="rotate-title"
  >
    <div class="card">
      <svg class="mark" viewBox="0 0 120 96" aria-hidden="true" focusable="false">
        <rect
          class="ghost"
          :x="PHONE_UPRIGHT.x"
          :y="PHONE_UPRIGHT.y"
          :width="PHONE_UPRIGHT.width"
          :height="PHONE_UPRIGHT.height"
          rx="5"
        />
        <rect
          class="target"
          :x="PHONE_SIDEWAYS.x"
          :y="PHONE_SIDEWAYS.y"
          :width="PHONE_SIDEWAYS.width"
          :height="PHONE_SIDEWAYS.height"
          rx="5"
        />
        <rect
          class="screen"
          :x="PHONE_SIDEWAYS.x + 5"
          :y="PHONE_SIDEWAYS.y + 5"
          :width="PHONE_SIDEWAYS.width - 14"
          :height="PHONE_SIDEWAYS.height - 10"
          rx="2"
        />
        <g class="turn">
          <path class="arc" d="M 20.5 33.6 A 42 42 0 0 1 99.5 33.6" />
          <polygon class="head" points="101.6,39.6 94.2,34.6 104.2,30.9" />
        </g>
      </svg>

      <p class="eyebrow">Orientation</p>
      <h1 id="rotate-title">Turn your device</h1>
      <p class="line">
        Steel of War is played sideways. There is no upright layout — the gun needs
        the width.
      </p>
    </div>
  </div>
</template>

<style scoped>
/*
 * Above everything, and it swallows every touch.
 *
 * `100` is the top of this app's stack — `ConfirmPurchase` is 100 and the reward
 * popup 99 — and this outranks both deliberately: a confirmation dialog behind a
 * rotate prompt is a purchase a player cannot see themselves agreeing to. The
 * pointer half is the containment: the arena's touch controls listen on the
 * canvas underneath, so a prompt that let a finger through would be a fire button
 * a player cannot see being pressed.
 *
 * `100dvh` rather than `100%`: a phone's `100vh` is the height *without* the URL
 * bar, so a prompt sized in `vh` overflows behind browser chrome — and this is
 * the one screen in the game that is deliberately reachable outside fullscreen.
 */
.rotate {
  position: fixed;
  inset: 0;
  z-index: 101;
  display: grid;
  place-items: center;
  height: 100dvh;
  padding: 1.5rem;
  background: var(--steel-900);
  color: var(--steel-200);
  pointer-events: auto;
  overscroll-behavior: contain;
}

.card {
  display: grid;
  justify-items: center;
  gap: 0.5rem;
  max-width: 24rem;
  text-align: center;
}

.mark {
  width: min(11rem, 42vw);
  height: auto;
  margin-bottom: 0.75rem;
  /* Never shrink to the room left over — a squashed phone stops reading as a
     phone, which is the note `GearIcon` carries about its teeth. */
  flex: none;
}

.ghost {
  fill: none;
  stroke: var(--steel-600);
  stroke-width: 2.5;
  stroke-dasharray: 5 4;
}

.target {
  fill: var(--steel-800);
  stroke: var(--steel-200);
  stroke-width: 3;
}

.screen {
  fill: var(--steel-700);
}

.turn {
  fill: var(--accent);
  stroke: var(--accent);
  /* Opacity only, on the one element whose absence costs nothing — the two
     phones are the message and they never move, so the picture is legible at
     any frame of this and the compositor owns the whole animation. */
  animation: rotate-hint 1.8s ease-in-out infinite;
}

.arc {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
}

.head {
  stroke-width: 1;
}

.eyebrow {
  margin: 0;
  color: var(--accent);
  font-size: 0.66rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

/* 400, not bold: Protest Guerrilla ships one weight and a browser asked for a
   heavier one synthesises it, closing the counters. The warning is `style.css`'s
   own, and this is a heading large enough for it to show. */
h1 {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(1.35rem, 6vw, 1.9rem);
  line-height: 1.1;
  color: var(--steel-200);
}

.line {
  margin: 0.25rem 0 0;
  max-width: 20rem;
  color: var(--steel-400);
  font-size: 0.82rem;
  line-height: 1.5;
}

/* The arc breathes. See `.turn` above. */
@keyframes rotate-hint {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
}

/* Reduced motion drops the movement, never the information: the arrow holds at
   full strength rather than disappearing. */
@media (prefers-reduced-motion: reduce) {
  .turn {
    animation: none;
    opacity: 1;
  }
}
</style>
